import { useState, useMemo } from 'react'
import { Search, Upload, RefreshCw, Camera } from '@/ui/icons'
import { Button, Badge, Card, WaveLoader } from '@/ui/components'
import { PageLayout } from '@/ui/layout'
import { cn } from '@/shared/utils'
import { useAllPhotos, TripPhotoGroup } from '@/modules/photos/hooks/useAllPhotos'
import { PhotoGrid } from '@/modules/photos/components/PhotoGrid'
import { PhotoLightbox } from '@/modules/photos/components/PhotoLightbox'
import { PhotoWithTrip } from '@/shared/api/photos'
import { PhotoInteractionsApi } from '@/shared/api'
import { useAuth } from '@/modules/auth'

type ViewMode = 'by-trip' | 'by-date' | 'all'

export function PhotosPage() {
  const { photos, tripGroups, totalCount, tripCount, isLoading, error, refetch } = useAllPhotos()
  const { user, getAccessToken } = useAuth()

  const [viewMode, setViewMode] = useState<ViewMode>('by-trip')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedTripId, setSelectedTripId] = useState<string>('all')

  // Lightbox state
  const [lightboxPhotos, setLightboxPhotos] = useState<PhotoWithTrip[]>([])
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)

  const photoInteractionsApi = useMemo(
    () => new PhotoInteractionsApi(async () => await getAccessToken()),
    [getAccessToken]
  )

  const matchesSearch = (photo: PhotoWithTrip) => {
    if (!searchQuery) return true
    const q = searchQuery.toLowerCase()
    return (
      photo.originalName?.toLowerCase().includes(q) ||
      photo.caption?.toLowerCase().includes(q) ||
      false
    )
  }

  // Flat filtered photos for by-date / all views
  const filteredPhotos = useMemo(() => {
    return photos.filter(p => {
      if (selectedTripId !== 'all' && p.tripId !== selectedTripId) return false
      return matchesSearch(p)
    })
  }, [photos, selectedTripId, searchQuery])

  // Filtered trip groups for by-trip view
  const filteredTripGroups = useMemo((): TripPhotoGroup[] => {
    return tripGroups
      .filter(g => selectedTripId === 'all' || g.tripId === selectedTripId)
      .map(g => ({ ...g, photos: g.photos.filter(matchesSearch) }))
      .filter(g => g.photos.length > 0)
  }, [tripGroups, selectedTripId, searchQuery])

  const openLightbox = (photo: PhotoWithTrip, scope: PhotoWithTrip[]) => {
    const idx = scope.findIndex(p => p.id === photo.id)
    setLightboxPhotos(scope)
    setSelectedIndex(idx >= 0 ? idx : 0)
  }

  const closeLightbox = () => {
    setSelectedIndex(null)
    setLightboxPhotos([])
  }

  const selectedPhoto = selectedIndex !== null ? lightboxPhotos[selectedIndex] : null

  const hasActiveFilter = searchQuery || selectedTripId !== 'all'
  const showEmpty = !isLoading && !error && totalCount === 0
  const showFilterEmpty = !isLoading && !error && totalCount > 0 && filteredPhotos.length === 0

  return (
    <PageLayout>
      <div className="h-full min-h-[calc(100dvh-4rem)] w-full bg-background">
        <div className="mx-auto flex h-full w-full max-w-[1400px] flex-col gap-6 px-4 py-6 md:px-8">

          {/* Header */}
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div>
                <h1 className="text-2xl font-bold tracking-tight text-foreground">Photos</h1>
                {!isLoading && !error && (
                  <p className="text-sm text-muted-foreground">
                    {totalCount === 0
                      ? 'No photos yet'
                      : `${totalCount} photo${totalCount !== 1 ? 's' : ''} across ${tripCount} trip${tripCount !== 1 ? 's' : ''}`}
                  </p>
                )}
              </div>
            </div>
            <Button
              disabled
              className="gap-2"
              title="Open a trip to upload photos"
            >
              <Upload className="h-4 w-4" />
              Upload
            </Button>
          </div>

          {/* Filter bar */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-1 items-center gap-2">
              <div className="relative flex-1 max-w-xs">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search photos..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="w-full rounded-lg border border-border bg-background pl-9 pr-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-adventure-500"
                />
              </div>
              <select
                value={selectedTripId}
                onChange={e => setSelectedTripId(e.target.value)}
                className="rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-adventure-500"
              >
                <option value="all">All trips</option>
                {tripGroups.map(g => (
                  <option key={g.tripId} value={g.tripId}>{g.tripName}</option>
                ))}
              </select>
            </div>

            {/* View toggle */}
            <div className="flex items-center rounded-lg border border-border bg-background p-1 gap-0.5">
              {(['by-trip', 'by-date', 'all'] as ViewMode[]).map(mode => (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  className={cn(
                    'rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                    viewMode === mode
                      ? 'bg-adventure-600 text-white'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  )}
                >
                  {mode === 'by-trip' ? 'By Trip' : mode === 'by-date' ? 'By Date' : 'All'}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          {isLoading && (
            <div className="flex flex-1 items-center justify-center py-24">
              <WaveLoader />
            </div>
          )}

          {error && (
            <div className="flex flex-1 flex-col items-center justify-center gap-4 py-24">
              <p className="text-sm text-destructive">{error}</p>
              <Button variant="outline" size="sm" onClick={refetch} className="gap-2">
                <RefreshCw className="h-4 w-4" />
                Retry
              </Button>
            </div>
          )}

          {showEmpty && (
            <div className="flex flex-1 items-center justify-center py-24">
              <Card className="w-full max-w-md border-border bg-card p-10 text-center">
                <Camera className="mx-auto mb-4 h-10 w-10 text-muted-foreground opacity-40" />
                <p className="text-sm font-medium text-foreground">No photos yet</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Upload photos from any trip to see them here.
                </p>
              </Card>
            </div>
          )}

          {showFilterEmpty && (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 py-24">
              <p className="text-sm text-muted-foreground">No photos match your search</p>
              {hasActiveFilter && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setSearchQuery(''); setSelectedTripId('all') }}
                >
                  Clear filters
                </Button>
              )}
            </div>
          )}

          {/* By Trip view */}
          {!isLoading && !error && totalCount > 0 && viewMode === 'by-trip' && filteredTripGroups.length > 0 && (
            <div className="flex flex-col gap-10">
              {filteredTripGroups.map(group => (
                <section key={group.tripId}>
                  <div className="mb-4 flex items-center gap-2">
                    <h2 className="text-base font-semibold text-foreground">{group.tripName}</h2>
                    <Badge variant="secondary">{group.photos.length}</Badge>
                  </div>
                  <PhotoGrid
                    photos={group.photos}
                    onPhotoClick={photo => openLightbox(photo as PhotoWithTrip, group.photos)}
                  />
                </section>
              ))}
            </div>
          )}

          {/* By Date / All views */}
          {!isLoading && !error && totalCount > 0 && viewMode !== 'by-trip' && filteredPhotos.length > 0 && (
            <PhotoGrid
              photos={filteredPhotos}
              onPhotoClick={photo => openLightbox(photo as PhotoWithTrip, filteredPhotos)}
            />
          )}

        </div>
      </div>

      {/* Lightbox */}
      {selectedPhoto && selectedIndex !== null && (
        <PhotoLightbox
          photo={selectedPhoto}
          photos={lightboxPhotos}
          currentIndex={selectedIndex}
          onClose={closeLightbox}
          onNavigate={setSelectedIndex}
          photoInteractionsApi={photoInteractionsApi}
          currentUserId={user?.id}
        />
      )}
    </PageLayout>
  )
}
